import { useState } from 'react';
import { Plus, Pencil, Trash2, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { usePipelinesSettings, type Pipeline } from '@/hooks/usePipelinesSettings';
import { PipelineStagesManager } from './PipelineStagesManager';
import { SettingsCard } from './SettingsCard';
import { useTranslation } from "react-i18next";

export function PipelinesTab() {
  const { t } = useTranslation("settings");
  const {
    pipelines,
    loading,
    createPipeline,
    updatePipeline,
    deletePipeline
  } = usePipelinesSettings();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedPipeline, setSelectedPipeline] = useState<Pipeline | null>(null);
  const [isStagesDialogOpen, setIsStagesDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: true
  });

  const handleOpenDialog = (pipeline?: Pipeline) => {
    if (pipeline) {
      setSelectedPipeline(pipeline);
      setFormData({
        name: pipeline.name,
        description: pipeline.description || '',
        is_active: pipeline.is_active
      });
    } else {
      setSelectedPipeline(null);
      setFormData({
        name: '',
        description: '',
        is_active: true
      });
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedPipeline(null);
    setFormData({
      name: '',
      description: '',
      is_active: true
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedPipeline) {
      await updatePipeline(selectedPipeline.id, formData);
    } else {
      await createPipeline(formData);
    }

    handleCloseDialog();
  };

  const handleDelete = async (id: string) => {
    if (confirm(t("pipelines.confirmDelete"))) {
      await deletePipeline(id);
    }
  };

  const handleOpenStages = (pipeline: Pipeline) => {
    setSelectedPipeline(pipeline);
    setIsStagesDialogOpen(true);
  };

  const handleCloseStages = () => {
    setIsStagesDialogOpen(false);
    setSelectedPipeline(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t("common.loading")}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-end items-center">
        <Button onClick={() => handleOpenDialog()} className="rounded-xl bg-[#2563EB] text-white shadow-sm hover:bg-[#1D4ED8] transition-colors duration-200">
          <Plus className="w-4 h-4 mr-2" />
          {t("pipelines.new")}
        </Button>
      </div>

      {pipelines.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-[#E5E7EB] rounded-xl dark:border-[#3F3F46]">
          <p className="text-[#64748B] dark:text-[#A1A1AA] mb-4">{t("pipelines.empty")}</p>
          <Button onClick={() => handleOpenDialog()} className="rounded-xl bg-[#2563EB] text-white shadow-sm hover:bg-[#1D4ED8] transition-colors duration-200">
            <Plus className="w-4 h-4 mr-2" />
            {t("pipelines.createFirst")}
          </Button>
        </div>
      ) : (
        <div className="grid gap-4">
          {pipelines.map((pipeline) => (
            <SettingsCard
              key={pipeline.id}
              className="p-4 hover:bg-[#F8FAFC] transition-colors duration-200 dark:hover:bg-[#3F3F46]/50"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h3 className="font-semibold text-[#0F172A] dark:text-[#FAFAFA]">{pipeline.name}</h3>
                    {!pipeline.is_active && (
                      <span className="text-xs bg-[#F1F5F9] text-[#64748B] px-2 py-1 rounded dark:bg-[#3F3F46] dark:text-[#A1A1AA]">{t("common.inactive")}</span>
                    )}
                  </div>
                  {pipeline.description && (
                    <p className="text-sm text-[#64748B] dark:text-[#A1A1AA] mt-1">
                      {pipeline.description}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleOpenStages(pipeline)}
                    className="text-[#0F172A] hover:bg-[#F1F5F9] dark:text-[#FAFAFA] dark:hover:bg-[#3F3F46]"
                  >
                    <ChevronRight className="w-4 h-4 mr-1" />
                    {t("pipelines.stages")}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleOpenDialog(pipeline)}
                    className="text-[#0F172A] hover:bg-[#F1F5F9] dark:text-[#FAFAFA] dark:hover:bg-[#3F3F46]"
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(pipeline.id)}
                    className="text-[#DC2626] dark:text-[#F87171] hover:bg-[#F1F5F9] dark:hover:bg-[#3F3F46] hover:text-[#B91C1C] dark:hover:text-[#F87171]"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </SettingsCard>
          ))}
        </div>
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="rounded-2xl bg-white border border-[#E5E7EB] text-[#0F172A] dark:bg-[#27272A] dark:border-[#3F3F46] dark:text-[#FAFAFA]">
          <DialogHeader>
            <DialogTitle className="text-[#0F172A] dark:text-[#FAFAFA]">
              {selectedPipeline ? t('pipelines.editPipeline') : t('pipelines.newPipeline')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-[#0F172A] dark:text-[#FAFAFA]">{t("common.nameRequired")}</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t("pipelines.namePlaceholder")}
                className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A] dark:focus-visible:border-[#60A5FA]"
                required
              />
            </div>

            <div>
              <Label htmlFor="description" className="text-[#0F172A] dark:text-[#FAFAFA]">{t("common.description")}</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={t("pipelines.descriptionPlaceholder")}
                className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A] dark:focus-visible:border-[#60A5FA]"
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="is_active" className="text-[#0F172A] dark:text-[#FAFAFA]">{t("common.active")}</Label>
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog} className="rounded-xl border border-[#E5E7EB] bg-white text-[#0F172A] hover:bg-[#F8FAFC] dark:border-[#3F3F46] dark:bg-transparent dark:text-[#FAFAFA] dark:hover:bg-[#3F3F46]">
                {t("common.cancel")}
              </Button>
              <Button type="submit" className="rounded-xl bg-[#2563EB] text-white shadow-sm hover:bg-[#1D4ED8] transition-colors duration-200">
                {selectedPipeline ? t('common:actions.save') : t('pipelines.create')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {selectedPipeline && (
        <PipelineStagesManager
          pipeline={selectedPipeline}
          open={isStagesDialogOpen}
          onClose={handleCloseStages}
        />
      )}
    </div>
  );
}
